import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, FileText, Users, Star, LogOut } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_decor-and-style/artifacts/89wv37zw_LOGO.png";

const navItems = [
  { label: 'Dashboard', path: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Articles', path: '/admin/articles', icon: FileText },
  { label: 'Audience', path: '/admin/audience', icon: Users },
  { label: 'Testimonials', path: '/admin/testimonials', icon: Star },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-layout">
      {/* Admin Top Bar */}
      <div className="h-14 border-b border-[#E5E7EB] flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Style and Space" className="w-8 h-8 rounded-full object-cover" />
          <span className="font-['Playfair_Display'] text-sm font-bold text-[#0A1128]">Style & Space</span>
          <span className="text-xs bg-[#0A1128] text-white px-2 py-0.5 uppercase tracking-widest ml-2">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-[#6B7280] hover:text-[#0A1128]" data-testid="view-site-link">
            View Site
          </a>
          <div className="flex items-center gap-2">
            <img src={user?.picture || ''} alt={user?.name} className="w-7 h-7 rounded-full object-cover border border-[#E5E7EB]" />
            <span className="text-sm text-[#0A1128] hidden md:inline">{user?.name}</span>
          </div>
          <button onClick={handleLogout} className="text-[#6B7280] hover:text-[#0A1128]" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 border-r border-[#E5E7EB] min-h-[calc(100vh-56px)] bg-[#FAFAFA] py-6 hidden md:block" data-testid="admin-sidebar">
          <nav className="space-y-1 px-3">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-[#0A1128] text-white'
                      : 'text-[#6B7280] hover:text-[#0A1128] hover:bg-white'
                  }`
                }
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile Nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] z-50 flex">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-3 text-xs ${
                  isActive ? 'text-[#0A1128]' : 'text-[#6B7280]'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </div>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8 lg:p-12 pb-20 md:pb-12 min-h-[calc(100vh-56px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
